import { titleFormat } from '@/constants/regexConstants';
import {
  type DisclosureTextSection,
  type DisclosureTextType,
  type FormatAgentTitleOptions,
} from '@/interfaces/PegaAgent';
import PegaApiStore from '@/stores/pegaApi.store';

export const removeHREFFromAnchors = (str: string) => {
  const tempdiv = document.createElement('div');
  tempdiv.innerHTML = str;

  const embeddedAnchors = Array.from(tempdiv.querySelectorAll('a'));
  embeddedAnchors.forEach((a) => {
    a.removeAttribute('href');
  });
  return tempdiv.innerHTML;
};

export const formatAgentTitle = (title: string, options?: FormatAgentTitleOptions) => {
  const value = options?.normalizeCase ? title.trim().toLowerCase() : title;
  return value.replace(titleFormat, (char: string) => char.toUpperCase()).replace('-', ' ');
};

export const formatMobileNumber = (mobilenumber: string | undefined): string => {
  if (!mobilenumber) return '';
  return `(${mobilenumber.slice(0, 3)}) ${mobilenumber.slice(3, 6)}-${mobilenumber.slice(6)}`;
};

export const parseDisclosureLineSections = (desc: string): DisclosureTextSection[] => {
  if (!desc) return [];

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = desc;

  const sections: DisclosureTextSection[] = [];

  const extractNodeTextWithBreaks = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent ?? '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const element = node as HTMLElement;
    if (element.tagName.toLowerCase() === 'br') {
      return '\n';
    }

    return Array.from(element.childNodes)
      .map((child) => extractNodeTextWithBreaks(child))
      .join('');
  };

  const pushSection = (text: string, type: DisclosureTextType, href?: string) => {
    if (!text) return;

    if (type === 'plain' && sections.length > 0) {
      const last = sections[sections.length - 1];
      if (last.type === type) {
        last.text += text;
        return;
      }
    }

    sections.push({ text, type, href });
  };

  const visitNode = (node: ChildNode) => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushSection(node.textContent ?? '', 'plain');
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();

    if (tag === 'br') {
      pushSection('\n', 'break');
      return;
    }

    if (tag === 'a') {
      const underlineNode = element.querySelector('u');
      const underlinedText = underlineNode ? extractNodeTextWithBreaks(underlineNode) : '';
      const anchorText = underlinedText || extractNodeTextWithBreaks(element);
      const href = element.getAttribute('href') ?? undefined;
      pushSection(anchorText, 'anchor', href);
      return;
    }

    Array.from(element.childNodes).forEach((child) => {
      visitNode(child);
    });
  };

  Array.from(tempDiv.childNodes).forEach((child) => {
    visitNode(child);
  });

  return sections;
};

export const formatDisclosureLine = (desc: string): string => {
  if (!desc) return '';

  let formatted = desc;

  const normalizedLeadingText = formatted
    .trimStart()
    .replace(/^<strong>/i, '')
    .trimStart();
  const startsWithNote = /^(Note|Nota|Remarque)\s*:/i.test(normalizedLeadingText);
  const hasStrongTags = /<strong>.*<\/strong>/i.test(formatted);

  if (startsWithNote && !hasStrongTags) {
    formatted = `<strong>${formatted}</strong>`;
  }

  const hasLineBreak = /<br\s*\/>/i.test(formatted);
  if (startsWithNote && !hasLineBreak) {
    formatted = `${formatted}<br/><br/>`;
  }

  return formatted;
};

export const formatDisclosuresLine = formatDisclosureLine;

export const detIframeHeight = (source: Event | HTMLIFrameElement): number => {
  const frame =
    source instanceof HTMLIFrameElement ? source : (source.target as HTMLIFrameElement | null);
  if (!frame) return 1;

  const doc = frame.contentDocument ?? frame.contentWindow?.document;
  if (!doc) return 1;

  const height = Math.max(
    doc.body.scrollHeight,
    doc.documentElement.scrollHeight,
    doc.body.offsetHeight,
    doc.documentElement.offsetHeight
  );
  frame.style.height = `${height}px`;
  frame.style.backgroundColor = 'white';
  return height;
};

export const composeEmail = function (message: string) {
  let composeEmailLink;
  if (message) {
    const mailSubject = encodeURIComponent('Contact Request');
    const customBody = encodeURIComponent(`Hi ${PegaApiStore().pegaCRMJSON?.agentInfo.firstname},

${message} 

Regards, 
${PegaApiStore().pegaCRMJSON?.agentInfo.firstname} ${PegaApiStore().pegaCRMJSON?.agentInfo.lastname}
${PegaApiStore().pegaCRMJSON?.agentInfo.email}`);
    composeEmailLink = `mailto:${PegaApiStore().pegaCRMJSON?.agentInfo.email}?subject=${mailSubject}&body=${customBody}`;
  } else {
    composeEmailLink = `mailto:${PegaApiStore().pegaCRMJSON?.agentInfo.email}`;
  }
  window.location.href = composeEmailLink;
};
